import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Copy, Check, Instagram } from "lucide-react";
import { contact, director } from "../../data/portfolio";

const ImdbGlyph = ({ className = "w-5 h-5" }) => (
  <span
    className={`inline-flex items-center justify-center font-sans font-semibold tracking-[0.02em] leading-none ${className}`}
    style={{ fontSize: "0.72em" }}
  >
    IMDb
  </span>
);

export const Contact = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(contact.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      // silent fail
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative pt-32 sm:pt-44 lg:pt-56 pb-16 sm:pb-20 px-6 sm:px-12 border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="text-[10px] uppercase tracking-[0.4em] text-white/40 mb-10 sm:mb-14 text-center"
        >
          <span style={{ color: "var(--accent-amber)" }}>05</span> — Collaboration
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
          className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.95] tracking-tighter text-white text-center max-w-5xl mx-auto"
        >
          Let&apos;s make
          <br />
          <span className="italic opacity-90">a film.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mt-10 sm:mt-14 text-base sm:text-lg leading-[1.6] text-white/60 font-sans max-w-2xl mx-auto text-center"
        >
          Looking for a director for your next film, commercial, music video or branded content? Let&apos;s connect and create something memorable.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.35, ease: [0.19, 1, 0.22, 1] }}
          className="h-px w-16 mx-auto mt-16 sm:mt-20"
          style={{ backgroundColor: "var(--accent-amber)" }}
        />

        {/* Email row — icon, address, copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.5, ease: [0.19, 1, 0.22, 1] }}
          className="mt-20 sm:mt-24 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
        >
          <a
            href={`mailto:${contact.email}`}
            data-testid="contact-email"
            className="group inline-flex items-center gap-4 sm:gap-5 text-white transition-colors duration-500"
          >
            <span
              className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 text-white/70 group-hover:text-white transition-all duration-500"
              style={{ color: "var(--accent-amber)" }}
            >
              <Mail strokeWidth={1.25} className="w-full h-full" />
            </span>
            <span className="font-serif italic text-xl sm:text-2xl lg:text-[28px] leading-none tracking-tight text-white/90 group-hover:text-white transition-colors duration-500 border-b border-white/15 group-hover:border-white/60 pb-2">
              {contact.email}
            </span>
          </a>

          <div className="relative">
            <button
              type="button"
              onClick={handleCopy}
              aria-label="Copy email address"
              data-testid="contact-email-copy"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/15 text-white/60 hover:text-white hover:border-white/60 transition-all duration-400 hover:-translate-y-0.5"
            >
              <AnimatePresence mode="wait" initial={false}>
                {copied ? (
                  <motion.span
                    key="check"
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.6 }}
                    transition={{ duration: 0.25 }}
                    style={{ color: "var(--accent-amber)" }}
                    className="inline-flex"
                  >
                    <Check className="w-4 h-4" strokeWidth={1.75} />
                  </motion.span>
                ) : (
                  <motion.span
                    key="copy"
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.6 }}
                    transition={{ duration: 0.25 }}
                    className="inline-flex"
                  >
                    <Copy className="w-4 h-4" strokeWidth={1.25} />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>

            <AnimatePresence>
              {copied && (
                <motion.span
                  key="copied-toast"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.3, ease: [0.19, 1, 0.22, 1] }}
                  className="absolute left-1/2 -translate-x-1/2 -bottom-7 text-[10px] uppercase tracking-[0.35em] whitespace-nowrap"
                  style={{ color: "var(--accent-amber)" }}
                  data-testid="contact-email-copied"
                >
                  Copied
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Social — two circular icons */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.7, ease: [0.19, 1, 0.22, 1] }}
          className="mt-16 sm:mt-20 flex items-center justify-center gap-8 sm:gap-10"
        >
          <a
            href={contact.imdb}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit IMDb profile"
            data-testid="contact-imdb"
            className="group relative inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-[color:var(--accent-amber)] transition-all duration-500 hover:-translate-y-1"
          >
            <span
              aria-hidden
              className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
              style={{
                boxShadow: "0 0 32px rgba(200,162,91,0.28)",
              }}
            />
            <ImdbGlyph className="w-7 h-7 sm:w-8 sm:h-8" />
          </a>

          <a
            href={contact.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Instagram profile"
            data-testid="contact-instagram"
            className="group relative inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-[color:var(--accent-amber)] transition-all duration-500 hover:-translate-y-1"
          >
            <span
              aria-hidden
              className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
              style={{
                boxShadow: "0 0 32px rgba(200,162,91,0.28)",
              }}
            />
            <Instagram strokeWidth={1.25} className="w-6 h-6 sm:w-7 sm:h-7" />
          </a>
        </motion.div>

        {/* Footer */}
        <div className="mt-28 sm:mt-40 pt-8 border-t border-white/10 grid grid-cols-12 gap-6">
          <div className="col-span-6 md:col-span-3">
            <p className="font-serif italic text-lg text-white/80">
              V<span className="opacity-60">.</span>R<span className="opacity-60">.</span>
            </p>
          </div>
          <div className="col-span-6 md:col-span-6 text-right md:text-left md:pl-16">
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">
              © {new Date().getFullYear()} {director.name}. All rights reserved.
            </p>
          </div>
          <div className="hidden md:block md:col-span-3 text-right">
            <a
              href="#top"
              data-testid="back-to-top"
              className="text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-white transition-colors duration-500"
            >
              Back to top ↑
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
